/**
 * CommitProgressBar — Wave 7 commit-pipeline visual progress.
 *
 * Renders the canonical 11-step ladder per design doc §3.1, decorated with
 * per-step status glyphs and humanized labels. Source-of-truth for label
 * text is `commitStepLabel(stepKey)` from `@retail-ops/shared-types/cart`
 * so the wire shape (closed-list step keys) and the customer-facing text
 * stay decoupled.
 *
 * NEVER surfaces the raw step key, error code, or step index in user-facing
 * text — keys are dev-only via `data-step-key` for QA + testing.
 *
 * Glyph contract (per brief §B):
 *   ✓  status='success'        — completed step
 *   ●  status='in-progress'    — active step (pulse animation)
 *   ⏸  status='paused'         — awaiting external action (signatures)
 *   ✕  status='failed'         — pipeline-error
 *   ⇢  status='skipped'        — step short-circuited (e.g. Test Mode)
 *   ○  status='pending' | none — not yet attempted
 */

import {
  COMMIT_PIPELINE_STEP_ORDER,
  commitStepLabel,
  type CommitPipelineStep,
  type CommitPipelineStepKey,
  type CommitPipelineStepStatus,
} from '@retail-ops/shared-types';
import './CommitProgressBar.css';

export interface CommitProgressBarProps {
  /**
   * Per-step rows from `CommitStatus.steps`. May be empty before the first
   * poll resolves — the bar still renders all 11 steps in `pending` state.
   */
  steps: ReadonlyArray<CommitPipelineStep>;
  /** Currently-active step (highlighted with pulse). Falls back to first non-success when omitted. */
  currentStepKey?: CommitPipelineStepKey;
  /** Compact mode for embedding on the cart card / status row. Default false. */
  compact?: boolean;
}

interface GlyphSpec {
  glyph: string;
  /** Human label used for sr-only text (read by screen readers). */
  srLabel: string;
  /** className suffix appended to .commit-pb__row. */
  toneClass: string;
}

function specForStatus(status: CommitPipelineStepStatus): GlyphSpec {
  switch (status) {
    case 'success':
      return { glyph: '✓', srLabel: 'completed', toneClass: 'is-success' };
    case 'in-progress':
      return { glyph: '●', srLabel: 'in progress', toneClass: 'is-active' };
    case 'paused':
      return { glyph: '⏸', srLabel: 'paused, awaiting external action', toneClass: 'is-paused' };
    case 'failed':
      return { glyph: '✕', srLabel: 'failed', toneClass: 'is-failed' };
    case 'skipped':
      return { glyph: '⇢', srLabel: 'skipped', toneClass: 'is-skipped' };
    case 'pending':
    default:
      return { glyph: '○', srLabel: 'pending', toneClass: 'is-pending' };
  }
}

export function CommitProgressBar({
  steps,
  currentStepKey,
  compact = false,
}: CommitProgressBarProps): JSX.Element {
  // Index incoming step rows by key so we can render the canonical order
  // even if the backend payload arrives unsorted (or partial).
  const byKey = new Map<CommitPipelineStepKey, CommitPipelineStep>();
  for (const s of steps) byKey.set(s.stepKey, s);

  // Resolve "active" key: explicit prop wins; otherwise first non-success
  // step in canonical order.
  const resolvedCurrent: CommitPipelineStepKey | undefined =
    currentStepKey
    ?? COMMIT_PIPELINE_STEP_ORDER.find((k) => {
      const row = byKey.get(k);
      if (!row) return true;
      return row.status !== 'success' && row.status !== 'skipped';
    });

  return (
    <ol
      className={`commit-pb${compact ? ' commit-pb--compact' : ''}`}
      aria-label="Commit pipeline progress"
    >
      {COMMIT_PIPELINE_STEP_ORDER.map((key, idx) => {
        const row = byKey.get(key);
        const status: CommitPipelineStepStatus =
          row?.status
          ?? (key === resolvedCurrent ? 'in-progress' : 'pending');
        const spec = specForStatus(status);
        const isActive = key === resolvedCurrent && status !== 'success';
        return (
          <li
            key={key}
            data-step-key={key}
            className={`commit-pb__row ${spec.toneClass}${isActive ? ' is-active-row' : ''}`.trim()}
            aria-current={isActive ? 'step' : undefined}
          >
            <span className="commit-pb__num" aria-hidden>{idx + 1}</span>
            <span className="commit-pb__glyph" aria-hidden>{spec.glyph}</span>
            <span className="commit-pb__label">{row?.detailLabel ?? commitStepLabel(key)}</span>
            <span className="commit-pb__sr-only">{spec.srLabel}</span>
          </li>
        );
      })}
    </ol>
  );
}
